import { RelativeDirectionSchema } from "@/services/otp/validations/planner.graphql"
import type { ReactElement, SVGProps } from "react"
import { AccessibleIcon } from "./AccessibleIcon"
import { Dot } from "lucide-react"

import { cn } from "@/lib/utils";

import {
  ArmpBus,
  ArmpClock,
  ArmpLocationPin,
  ArmpWarning,
  Ferry,
  Walk
} from './icons.custom'

import {
  RiAddCircleFill,
  RiAddFill,
  RiAnticlockwiseFill,
  RiArrowDownSFill,
  RiArrowGoBackFill,
  RiArrowLeftRightFill,
  RiArrowLeftSFill,
  RiArrowLeftUpFill,
  RiArrowRightFill,
  RiArrowRightSFill,
  RiArrowRightUpFill,
  RiArrowUpDownFill,
  RiArrowUpFill,
  RiArrowUpSFill,
  RiBikeFill,
  RiBookFill,
  RiCarFill,
  RiCheckFill,
  RiCheckboxCircleFill,
  RiCircleFill,
  RiCircleLine,
  RiClockwiseFill,
  RiCloseFill,
  RiCornerUpLeftFill,
  RiCornerUpRightFill,
  RiDownloadFill,
  RiEditBoxFill,
  RiEmojiStickerFill,
  RiEyeFill,
  RiEyeOffFill,
  RiFlagFill,
  RiForbid2Fill,
  RiFullscreenFill,
  RiCalendarFill,
  RiHospitalFill,
  RiLinkM,
  RiLoader4Fill,
  RiLogoutBoxRFill,
  RiRoadMapFill,
  RiMore2Fill,
  RiPhoneFill,
  RiQuestionFill,
  RiRouteFill,
  RiSearchFill,
  RiSendPlaneFill,
  RiServerLine,
  RiShareFill,
  RiSubtractFill,
  RiTrainFill,
  RiUploadFill,
} from "@remixicon/react"

type IconProps = SVGProps<SVGSVGElement>

export const Icons = {
  actions: {
    back: (props: IconProps) => (
      <AccessibleIcon Icon={RiArrowLeftSFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    close: (props: IconProps) => (
      <AccessibleIcon Icon={RiCloseFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    share: (props: IconProps) => (
      <AccessibleIcon Icon={RiShareFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    download: (props: IconProps) => (
      <AccessibleIcon Icon={RiDownloadFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    upload: (props: IconProps) => (
      <AccessibleIcon Icon={RiUploadFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    expand: (props: IconProps) => (
      <AccessibleIcon Icon={RiFullscreenFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    more: (props: IconProps) => (
      <AccessibleIcon Icon={RiMore2Fill} decorative color="currentColor" className={props.className} {...props} />
    ),
    swap: (props: IconProps) => (
      <AccessibleIcon Icon={RiArrowUpDownFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    swapHorizontal: (props: IconProps) => (
      <AccessibleIcon Icon={RiArrowLeftRightFill} decorative color="currentColor" className={props.className} {...props} />
    ),
  },
  input: {
    error: ({ className, ...props }: IconProps) => (
      <AccessibleIcon Icon={ArmpWarning} decorative className={cn("size-4", className)} {...props} />
    ),
    append: (props: IconProps) => (
      <AccessibleIcon Icon={RiAddCircleFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    selected: (props: IconProps) => (
      <AccessibleIcon Icon={RiCheckFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    passwordVisible: (props: IconProps) => (
      <AccessibleIcon Icon={RiEyeFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    passwordHidden: (props: IconProps) => (
      <AccessibleIcon Icon={RiEyeOffFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    plus: (props: IconProps) => (
      <AccessibleIcon Icon={RiAddFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    minus: (props: IconProps) => (
      <AccessibleIcon Icon={RiSubtractFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    search: (props: IconProps) => (
      <AccessibleIcon Icon={RiSearchFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    expand: (props: IconProps) => (
      <AccessibleIcon Icon={RiArrowDownSFill} decorative color="currentColor" className={props.className} {...props} />
    ),
  },
  alerts: {
    error: ({ className, ...props }: IconProps) => (
      <AccessibleIcon Icon={ArmpWarning} decorative className={cn("w-3 h-3", className)} {...props} />
    ),
    success: ({ className, ...props }: IconProps) => (
      <AccessibleIcon Icon={RiCheckboxCircleFill} decorative color="currentColor" className={cn("w-3 h-3", className)} {...props} />
    ),
    default: ({ className, ...props }: IconProps) => (
      <AccessibleIcon Icon={ArmpWarning} decorative className={cn("w-3 h-3", className)} {...props} />
    ),
  },
  general: {
    logOut: (props: IconProps) => (
      <AccessibleIcon Icon={RiLogoutBoxRFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    send: (props: IconProps) => (
      <AccessibleIcon Icon={RiSendPlaneFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    map: (props: IconProps) => (
      <AccessibleIcon Icon={RiRoadMapFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    notebookPen: (props: IconProps) => (
      <AccessibleIcon Icon={RiEditBoxFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    helpCircle: (props: IconProps) => (
      <AccessibleIcon Icon={RiQuestionFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    book: (props: IconProps) => (
      <AccessibleIcon Icon={RiBookFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    flag: (props: IconProps) => (
      <AccessibleIcon Icon={RiFlagFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    sticker: (props: IconProps) => (
      <AccessibleIcon Icon={RiEmojiStickerFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    chevronUp: (props: IconProps) => (
      <AccessibleIcon Icon={RiArrowUpSFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    chevronDown: (props: IconProps) => (
      <AccessibleIcon Icon={RiArrowDownSFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    chevronLeft: (props: IconProps) => (
      <AccessibleIcon Icon={RiArrowLeftSFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    chevronRight: (props: IconProps) => (
      <AccessibleIcon Icon={RiArrowRightSFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    loader: (props: IconProps) => (
      <AccessibleIcon Icon={RiLoader4Fill} decorative color="currentColor" className={props.className} {...props} />
    ),
    x: (props: IconProps) => (
      <AccessibleIcon Icon={RiCloseFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    dot: (props: IconProps) => (
      <AccessibleIcon Icon={Dot} decorative color="currentColor" className={props.className} {...props} />
    ),
    calendar: ({ className, ...props }: IconProps) => (
      <AccessibleIcon Icon={RiCalendarFill} decorative className={cn("size-4", className)} {...props} />
    ),
  },
  navigation: {
    arrowRight: (props: IconProps) => (
      <AccessibleIcon Icon={RiArrowRightFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    arrowUpRight: (props: IconProps) => (
      <AccessibleIcon Icon={RiArrowRightUpFill} decorative color="currentColor" className={props.className} {...props} />
    ),
  },
  primitives: {
    check: (props: IconProps) => (
      <AccessibleIcon Icon={RiCheckFill} decorative color="currentColor" className={props.className} {...props} />
    ),
    circle: (props: IconProps) => (
      <AccessibleIcon Icon={RiCircleFill} decorative color="currentColor" className={props.className} {...props} />
    ),
  },
  plan: {
    loading: ({ className, ...props }: IconProps) => (
      <AccessibleIcon Icon={RiLoader4Fill} label="Loading" className={cn("w-32 h-32 animate-spin duration-1000", className)} color="currentColor" {...props} />
    ),
    empty: ({ className, ...props }: IconProps) => (
      <AccessibleIcon Icon={ArmpLocationPin} label="No results" className={cn("w-32 h-32", className)} {...props} />
    ),
    error: ({ className, ...props }: IconProps) => (
      <AccessibleIcon Icon={RiServerLine} label="Error" className={cn("w-32 h-32", className)} color="currentColor" {...props} />
    ),
    noRoute: ({ className, ...props }: IconProps) => (
      <AccessibleIcon Icon={RiForbid2Fill} label="No route available" className={cn("w-32 h-32", className)} color="currentColor" {...props} />
    ),
  },
  itinerary: {
    car: ({ className, ...props }: IconProps) => (
      <AccessibleIcon Icon={RiCarFill} decorative color="currentColor" className={cn("w-4 h-4", className)} {...props} />
    ),
    bike: ({ className, ...props }: IconProps) => (
      <AccessibleIcon Icon={RiBikeFill} decorative color="currentColor" className={cn("w-4 h-4", className)} {...props} />
    ),
    train: ({ className, ...props }: IconProps) => (
      <AccessibleIcon Icon={RiTrainFill} decorative color="currentColor" className={cn("w-4 h-4", className)} {...props} />
    ),
    bus: ({ className, ...props }: IconProps) => (
      <AccessibleIcon Icon={ArmpBus} decorative className={cn("w-4 h-4", className)} {...props} />
    ),
    rail: ({ className, ...props }: IconProps) => (
      <AccessibleIcon Icon={ArmpBus} decorative className={cn("w-4 h-4", className)} {...props} />
    ),
    walk: ({ className, ...props }: IconProps) => (
      <AccessibleIcon Icon={Walk} decorative className={cn("w-4 h-4", className)} {...props} />
    ),
    ferry: ({ className, ...props }: IconProps) => (
      <AccessibleIcon Icon={Ferry} decorative className={cn("w-4 h-4", className)} {...props} />
    ),

    stop: ({ className, ...props }: IconProps) => <AccessibleIcon Icon={RiCircleFill} decorative className={cn("size-3", className)} color="currentColor" {...props} />,
    move: ({ className, ...props }: IconProps) => (
      <AccessibleIcon Icon={RiCircleLine} decorative color="currentColor" className={cn("w-4 h-4 stroke-2", className)} {...props} />
    ),
    transfer: ({ className, ...props }: IconProps) => (
      <AccessibleIcon Icon={RiArrowRightSFill} decorative color="currentColor" className={cn("w-4 h-4 stroke-2", className)} {...props} />
    ),

    indicator: {
      medical: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={RiHospitalFill} decorative color="currentColor" className={cn("h-4 w-4 md:h-5 md:w-5", className)} {...props} />
      ),
      callAhead: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={ArmpClock} decorative className={cn("h-4 w-4 md:h-5 md:w-5", className)} {...props} />
      ),
      scheduledDayOf: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={RiCalendarFill} decorative color="currentColor" className={cn("h-4 w-4 md:h-5 md:w-5", className)} {...props} />
      ),
      estimatedTimes: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={ArmpClock} decorative className={cn("h-4 w-4 md:h-5 md:w-5", className)} {...props} />
      ),
      multipleTransfers: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={RiArrowLeftRightFill} decorative color="currentColor" className={cn("h-4 w-4 md:h-5 md:w-5", className)} {...props} />
      ),

      phone: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={RiPhoneFill} decorative color="currentColor" className={cn("h-4 w-4 md:h-5 md:w-5", className)} {...props} />
      ),
      agencyUrl: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={RiLinkM} decorative color="currentColor" className={cn("h-4 w-4 md:h-5 md:w-5", className)} {...props} />
      ),
      flex: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={RiRouteFill} decorative color="currentColor" className={cn("h-4 w-4 md:h-5 md:w-5", className)} {...props} />
      ),
      departure: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={ArmpClock} decorative className={cn("h-4 w-4 md:h-5 md:w-5", className)} {...props} />
      ),
      booking: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={RiBookFill} decorative color="currentColor" className={cn("h-4 w-4 md:h-5 md:w-5", className)} {...props} />
      ),
      operatingHours: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={RiCalendarFill} decorative className={cn("h-4 w-4 md:h-5 md:w-5", className)} {...props} />
      ),
      link: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={RiLinkM} decorative color="currentColor" className={cn("h-3 w-3 md:h-3 md:w-3", className)} {...props} />
      ),
    },
    directions: {
      DEPART: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={RiArrowUpFill} decorative color="currentColor" className={cn("h-4 w-4 md:h-5 md:w-5", className)} {...props} />
      ),
      CONTINUE: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={RiArrowUpFill} decorative color="currentColor" className={cn("h-4 w-4 md:h-5 md:w-5", className)} {...props} />
      ),
      ENTER_STATION: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={RiArrowUpFill} decorative color="currentColor" className={cn("h-4 w-4 md:h-5 md:w-5", className)} {...props} />
      ),
      EXIT_STATION: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={RiArrowUpFill} decorative color="currentColor" className={cn("h-4 w-4 md:h-5 md:w-5", className)} {...props} />
      ),
      FOLLOW_SIGNS: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={RiArrowUpFill} decorative color="currentColor" className={cn("h-4 w-4 md:h-5 md:w-5", className)} {...props} />
      ),
      ELEVATOR: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={RiArrowUpSFill} decorative color="currentColor" className={cn("h-4 w-4 md:h-5 md:w-5", className)} {...props} />
      ),
      HARD_LEFT: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={RiCornerUpLeftFill} decorative color="currentColor" className={cn("h-4 w-4 md:h-5 md:w-5", className)} {...props} />
      ),
      HARD_RIGHT: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={RiCornerUpRightFill} decorative color="currentColor" className={cn("h-4 w-4 md:h-5 md:w-5", className)} {...props} />
      ),
      LEFT: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={RiCornerUpLeftFill} decorative color="currentColor" className={cn("h-4 w-4 md:h-5 md:w-5", className)} {...props} />
      ),
      RIGHT: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={RiCornerUpRightFill} decorative color="currentColor" className={cn("h-4 w-4 md:h-5 md:w-5", className)} {...props} />
      ),
      SLIGHTLY_LEFT: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={RiArrowLeftUpFill} decorative color="currentColor" className={cn("h-4 w-4 md:h-5 md:w-5", className)} {...props} />
      ),
      SLIGHTLY_RIGHT: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={RiArrowRightUpFill} decorative color="currentColor" className={cn("h-4 w-4 md:h-5 md:w-5", className)} {...props} />
      ),
      UTURN_LEFT: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={RiArrowGoBackFill} decorative color="currentColor" className={cn("h-4 w-4 -rotate-90 md:h-5 md:w-5", className)} {...props} />
      ),
      UTURN_RIGHT: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={RiArrowGoBackFill} decorative color="currentColor" className={cn("h-4 w-4 rotate-90 -scale-x-100 md:h-5 md:w-5", className)} {...props} />
      ),
      CIRCLE_CLOCKWISE: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={RiClockwiseFill} decorative color="currentColor" className={cn("h-4 w-4 md:h-5 md:w-5", className)} {...props} />
      ),
      CIRCLE_COUNTERCLOCKWISE: ({ className, ...props }: IconProps) => (
        <AccessibleIcon Icon={RiAnticlockwiseFill} decorative color="currentColor" className={cn("h-4 w-4 md:h-5 md:w-5", className)} {...props} />
      ),
    } satisfies Record<RelativeDirectionSchema, ({ className, ...props }: IconProps) => ReactElement>
  }
} as const
